import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminSelfProfile } from 'src/queries/adminProfile';
import { useAuthSessionRevision } from 'src/auth/sessionEvents';

import Swal from 'sweetalert2';
import { SidebarContext } from 'src/contexts/SidebarContext';
import {
  Bell,
  BookUser,
  BriefcaseBusiness,
  ChevronDown,
  History,
  LogOut,
  Menu,
  MessageCircle,
  Settings,
  User,
  Users,
  X
} from 'lucide-react';

import HeaderButtons from '../Buttons';
import { config } from 'src/config';
import { logout } from 'src/helper/api_helper';

interface HeaderUserboxProps {
  showNavigationMenu?: boolean;
  companyProfilePath?: string | null;
  workspaceRole?: string;
  platformCompanyLinks?: {
    workforce: string;
    clientProfiles: string;
    positions: string;
    changeHistory: string;
  } | null;
}

export const formatAdminRoleLabel = (role?: string): string => {
  if (role === 'SUPER_ADMIN') {
    return 'Global Admin';
  }
  return role?.replace(/_/g, ' ') ?? '';
};

function HeaderUserbox({
  showNavigationMenu = true,
  companyProfilePath,
  workspaceRole,
  platformCompanyLinks
}: HeaderUserboxProps) {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [avatarBroken, setAvatarBroken] = useState<boolean>(false);
  const [, refreshProfile] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useAuthSessionRevision();
  const localUser = localStorage.getItem('PLS');
  const user = localUser ? JSON.parse(localUser) : null;
  const profileQuery = useQuery({
    queryKey: ['admin-self-profile'],
    queryFn: getAdminSelfProfile,
    enabled: user?.authMode === 'admin-realm',
    retry: false
  });
  const profile = user?.authMode === 'admin-realm'
    ? (profileQuery.data?.id === (user?.principal?.id ?? user?.user?.id) ? profileQuery.data : user?.user)
    : user?.user;
  const profileImage: string | null = profile?.profileImage || null;
  const firstName: string = profile?.firstName || '';
  const lastName: string = profile?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  useEffect(() => {
    const handleProfileUpdated = () => {
      setAvatarBroken(false);
      refreshProfile((value) => value + 1);
    };
    window.addEventListener('admin-profile-updated', handleProfileUpdated);
    return () =>
      window.removeEventListener('admin-profile-updated', handleProfileUpdated);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = () => {
    setOpen(false);
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, sign out!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        await logout();
      }
    });
  };

  const dropdownItems = useMemo(() => {
    if (platformCompanyLinks) {
      return [
        {
          path: '/management/profile/details',
          label: 'My Profile',
          icon: <User size={18} />
        },
        {
          path: companyProfilePath!,
          label: 'Company Profile',
          icon: <BookUser size={18} />
        },
        {
          path: platformCompanyLinks.workforce,
          label: 'Company Workforce',
          icon: <Users size={18} />
        },
        {
          path: platformCompanyLinks.clientProfiles,
          label: 'Client Profiles',
          icon: <BriefcaseBusiness size={18} />
        },
        {
          path: platformCompanyLinks.changeHistory,
          label: 'Change History',
          icon: <History size={18} />
        }
      ];
    }
    return [
      {
        path: '/management/profile/details',
        label: 'My Profile',
        icon: <User size={18} />
      },
      {
        path: companyProfilePath ?? '/dashboards/company-profile',
        label: 'Company Profile',
        icon: <BookUser size={18} />
      },
      {
        path: '/dashboards/messenger/support',
        label: 'Help & Support',
        icon: <MessageCircle size={18} />
      },
      {
        path: '/dashboards/activites',
        label: 'Activity Logs',
        icon: <Bell size={18} />
      },
      {
        path: '/dashboards/settings',
        label: 'Settings',
        icon: <Settings size={18} />
      }
    ];
  }, [companyProfilePath, platformCompanyLinks]);

  return (
    <>
      <div className="flex shrink-0 items-center gap-3">
        <HeaderButtons companyProfilePath={companyProfilePath} />

        <div
          ref={dropdownRef}
          className="relative ml-1 border-l border-white/10 pl-3"
        >
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center gap-3 rounded-[18px] border border-white/10 bg-white/[0.03] px-3 py-2 text-left transition-all duration-200 hover:bg-white/[0.07]"
            type="button"
          >
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-white/10">
              {profileImage && !avatarBroken ? (
                <img
                  src={/^https?:\/\//i.test(profileImage) ? profileImage : config.FILe_URL + profileImage}
                  alt={firstName || 'User'}
                  onError={() => setAvatarBroken(true)}
                  className="h-full w-full object-cover"
                />
              ) : initials ? (
                <span className="text-sm font-semibold text-white">
                  {initials}
                </span>
              ) : (
                <User size={17} className="text-white" />
              )}
            </div>
            <div className="hidden min-w-0 sm:block">
              <p className="truncate text-sm font-medium text-white">
                {firstName} {lastName}
              </p>
              <p className="truncate text-[11px] font-medium uppercase tracking-[0.12em] text-white/[0.52]">
                {formatAdminRoleLabel(workspaceRole ?? user?.user?.Role?.name)}
              </p>
            </div>
            <ChevronDown
              size={16}
              className={`shrink-0 text-white/70 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isOpen && (
            <div className="absolute right-0 mt-3 w-56 overflow-hidden rounded-2xl border border-[#d8c286]/20 bg-[#111319] p-2 shadow-[0_18px_48px_rgba(0,0,0,0.35)]">
              {dropdownItems.map((item) => {
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-200 ${
                      isActive
                        ? 'bg-[#f3c449] text-[#16120a]'
                        : 'text-white/[0.82] hover:bg-white/[0.06] hover:text-white'
                    }`}
                  >
                    {item.icon}
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
              <button
                onClick={handleSignOut}
                className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/[0.82] transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                type="button"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>

        <button
          style={{ visibility: showNavigationMenu ? 'visible' : 'hidden' }}
          onClick={toggleSidebar}
          aria-label={
            sidebarToggle ? 'Close navigation menu' : 'Open navigation menu'
          }
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-white transition-colors duration-200 hover:bg-white/[0.08]"
          type="button"
        >
          {sidebarToggle ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
    </>
  );
}

export default HeaderUserbox;
